import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { getCurrentUser } from '../components/api/services/userService/User';
import { getValidToken } from '../utils/tokenUtils';
import { getRoleValue } from '../utils/roleUtils';

const UserContext = createContext();
const emptyPermissions = {
  isSuperAdmin: false,
  isAdmin: false,
  hasClientAccess: false,
  hasDepartmentAccess: false,
  hasTeamAccess: false,
  clientIds: [],
  departmentIds: [],
  teamIds: [],
  clientRole: null,
  departmentRoles: {},
  teamRoles: {},
  onlyServiceRoleUser: false
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();
  const [user, setUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userTypeDetermined, setUserTypeDetermined] = useState(false);
  const fetchingRef = useRef(false);
  const hasFetchedRef = useRef(false);

  const fetchUser = async () => {
    if (fetchingRef.current || hasFetchedRef.current) {
      return;
    }

    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      const token = await getValidToken(keycloak);
      const response = await getCurrentUser(token);

      if (response.success) {
        setUser(response.user);

        // Build permissions object based on user data - optimized version
        const userData = response.user;
        const permissions = {
          isSuperAdmin: userData.superAdmin || false,
          isAdmin: false,
          hasClientAccess: false,
          hasDepartmentAccess: false,
          hasTeamAccess: false,
          clientIds: [],
          departmentIds: [],
          teamIds: [],
          clientRole: null,
          departmentRoles: {},
          teamRoles: {},
          onlyServiceRoleUser: userData.onlyServiceRoleUser || false
        };

        const currentUserId = response.user.id;

        if (response.user.client?.client) {
          permissions.hasClientAccess = true;
          permissions.clientIds.push(response.user.client.client.id);
          permissions.clientRole = getRoleValue(response.user.client.client, 'client');
          const isClientAdmin = permissions.clientRole === 'admin' ||
            response.user.client.admins?.some(admin => admin.id === currentUserId);
          permissions.isAdmin = permissions.isAdmin || isClientAdmin;
        }

        if (response.user.departments?.length > 0) {
          permissions.hasDepartmentAccess = true;
          response.user.departments.forEach(dept => {
            if (dept.department) {
              permissions.departmentIds.push(dept.department.id);
              const deptRole = getRoleValue(dept.department, 'department');
              permissions.departmentRoles[dept.department.id] = deptRole;
              const isDeptAdmin = deptRole === 'admin' ||
                dept.admins?.some(admin => admin.id === currentUserId);
              permissions.isAdmin = permissions.isAdmin || isDeptAdmin;
            }
          });
        }

        if (response.user.teams?.length > 0) {
          permissions.hasTeamAccess = true;
          response.user.teams.forEach(team => {
            if (team.team) {
              permissions.teamIds.push(team.team.id);
              const teamRole = getRoleValue(team.team, 'team');
              permissions.teamRoles[team.team.id] = teamRole;
              const isTeamAdmin = teamRole === 'admin' || teamRole === 'lead' ||
                team.admins?.some(admin => admin.id === currentUserId);
              permissions.isAdmin = permissions.isAdmin || isTeamAdmin;
            }
          });
        }

        // Super admin has all permissions
        if (permissions.isSuperAdmin) {
          permissions.isAdmin = true;
          permissions.hasClientAccess = true;
          permissions.hasDepartmentAccess = true;
          permissions.hasTeamAccess = true;
        } else {
          // Check client permissions
          const client = userData.client?.client;
          if (client) {
            permissions.hasClientAccess = true;
            permissions.clientIds.push(client.id);
            permissions.clientRole = getRoleValue(client, 'client');
            if (permissions.clientRole === 'admin') permissions.isAdmin = true;
          }

          // Check department permissions
          const departments = userData.departments;
          if (departments?.length > 0) {
            permissions.hasDepartmentAccess = true;
            for (const dept of departments) {
              if (dept.department) {
                permissions.departmentIds.push(dept.department.id);
                const deptRole = getRoleValue(dept.department, 'department');
                permissions.departmentRoles[dept.department.id] = deptRole;
                if (deptRole === 'admin') permissions.isAdmin = true;
              }
            }
          }

          // Check team permissions
          const teams = userData.teams;
          if (teams?.length > 0) {
            permissions.hasTeamAccess = true;
            for (const team of teams) {
              if (team.team) {
                permissions.teamIds.push(team.team.id);
                const teamRole = getRoleValue(team.team, 'team');
                permissions.teamRoles[team.team.id] = teamRole;
                if (teamRole === 'admin') permissions.isAdmin = true;
              }
            }
          }
        }

        setUserPermissions(permissions);
        setUserTypeDetermined(true);
        hasFetchedRef.current = true;
      } else {
        setError(response.error || 'Failed to fetch user data');
        setUserPermissions(emptyPermissions);
        setUserTypeDetermined(true);
        hasFetchedRef.current = true;
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      
      if (err.message?.includes('Session expired') || err.message?.includes('not authenticated')) {
        keycloak.logout({ redirectUri: window.location.origin });
        return;
      }
      
      setError(err.message || 'Failed to fetch user data');
      setUserPermissions(emptyPermissions);
      setUserTypeDetermined(true);
      hasFetchedRef.current = true;
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  const refetchUser = () => {
    if (keycloak.authenticated) {
      hasFetchedRef.current = false;
      fetchingRef.current = false;
      fetchUser();
    }
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    return user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email || '';
  };

  const getUserRoles = () => {
    if (!userPermissions) return [];
    
    const roles = [];
    
    if (userPermissions.isSuperAdmin) {
      roles.push({ type: 'global', label: 'Super Admin', priority: 1 });
    }
    
    if (userPermissions.clientRole) {
      const roleLabel = userPermissions.clientRole === 'admin' ? 'Client Admin' : 'Client Member';
      roles.push({ type: 'client', label: roleLabel, priority: 2 });
    }
    
    const deptRoles = Object.values(userPermissions.departmentRoles || {});
    if (deptRoles.some(r => r === 'admin')) {
      roles.push({ type: 'department', label: 'Department Admin', priority: 3 });
    } else if (deptRoles.length > 0) {
      roles.push({ type: 'department', label: 'Department Member', priority: 4 });
    }
    
    const teamRoles = Object.values(userPermissions.teamRoles || {});
    if (teamRoles.some(r => r === 'lead')) {
      roles.push({ type: 'team', label: 'Team Lead', priority: 5 });
    } else if (teamRoles.some(r => r === 'admin')) {
      roles.push({ type: 'team', label: 'Team Admin', priority: 5 });
    } else if (teamRoles.length > 0) {
      roles.push({ type: 'team', label: 'Team Member', priority: 6 });
    }
    
    if (roles.length === 0) {
      roles.push({ type: 'user', label: 'User', priority: 7 });
    }
    
    return roles.sort((a, b) => a.priority - b.priority);
  };

  const getPrimaryRole = () => {
    const roles = getUserRoles();
    return roles.length > 0 ? roles[0].label : 'User';
  };

  const hasAccess = useMemo(() => (feature) => {
    if (!userPermissions) return false;

    switch (feature) {
      case 'admin':
      case 'nodeMonitoring':
        return userPermissions.isSuperAdmin || userPermissions.isAdmin;
      case 'cloudManagement':
        // Allow access to cloud management for users with client access (admins and members)
        return userPermissions.isSuperAdmin || userPermissions.isAdmin || userPermissions.hasClientAccess;
      case 'marketplace':
      case 'appPreview':
      case 'apphub':
      case 'dashboard':
        return true; // Allow all authenticated users to access these
      default:
        return true; // Default access for basic features
    }
  }, [userPermissions]);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    if (keycloak.authenticated) {
      fetchUser();
    } else {
      setUser(null);
      setUserPermissions(null);
      setUserTypeDetermined(false);
      setLoading(false);
      setError(null);
      hasFetchedRef.current = false;
      fetchingRef.current = false;
    }
  }, [initialized, keycloak.authenticated]);

  const contextValue = useMemo(() => ({
    user,
    userPermissions,
    loading,
    error,
    refetchUser,
    getUserDisplayName,
    getUserRoles,
    getPrimaryRole,
    hasAccess,
    userTypeDetermined,

    isAuthenticated: keycloak.authenticated,
    isSuperAdmin: userPermissions?.isSuperAdmin || false,
    isAdmin: userPermissions?.isAdmin || false,
    onlyServiceRoleUser: userTypeDetermined ? (userPermissions?.onlyServiceRoleUser || false) : undefined
  }), [user, userPermissions, loading, error, userTypeDetermined, keycloak.authenticated]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};